import React, { useState } from 'react';
import { ArrowLeft, Save, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';

const FOSegmentForm = ({ onBack, segmentData = null, mode = 'add' }) => {
  const [formData, setFormData] = useState({
    spanId: segmentData?.spanId || '',
    projectName: segmentData?.projectName || '',
    segmentType: segmentData?.segmentType || 'Insert',
    kota: segmentData?.kota || '',
    province: segmentData?.province || '',
    region: segmentData?.region || 'CENTRAL',
    vendor: segmentData?.vendor || '',
    siteIdA: segmentData?.siteIdA || '',
    siteNameA: segmentData?.siteNameA || '',
    longA: segmentData?.longA || '',
    latA: segmentData?.latA || '',
    towerOwnerA: segmentData?.towerOwnerA || '',
    siteIdB: segmentData?.siteIdB || '',
    siteNameB: segmentData?.siteNameB || '',
    longB: segmentData?.longB || '',
    latB: segmentData?.latB || '',
    towerOwnerB: segmentData?.towerOwnerB || '',
    description: segmentData?.description || '',
    lengthPlan: segmentData?.lengthPlan || '',
    planDistance: segmentData?.planDistance || '',
    projectYear: segmentData?.projectYear || new Date().getFullYear(),
    spkDate: segmentData?.spkDate || ''
  });

  const [expandedSection, setExpandedSection] = useState('basic');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.spanId.trim()) newErrors.spanId = 'Span ID is required';
    if (!formData.projectName.trim()) newErrors.projectName = 'Project Name is required';
    if (!formData.siteIdA.trim()) newErrors.siteIdA = 'Site ID A is required';
    if (!formData.siteIdB.trim()) newErrors.siteIdB = 'Site ID B is required';
    if (!formData.lengthPlan) newErrors.lengthPlan = 'Length Plan is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert(`Segment ${mode === 'add' ? 'created' : 'updated'} successfully!`);
      if (onBack) onBack();
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      spanId: '',
      projectName: '',
      segmentType: 'Insert',
      kota: '',
      province: '',
      region: 'CENTRAL',
      vendor: '',
      siteIdA: '',
      siteNameA: '',
      longA: '',
      latA: '',
      towerOwnerA: '',
      siteIdB: '',
      siteNameB: '',
      longB: '',
      latB: '',
      towerOwnerB: '',
      description: '',
      lengthPlan: '',
      planDistance: '',
      projectYear: new Date().getFullYear(),
      spkDate: ''
    });
    setErrors({});
    setExpandedSection('basic');
  };

  const Section = ({ title, name, children }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => toggleSection(name)}
        className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${
          expandedSection === name 
            ? 'bg-gradient-to-r from-blue-50 to-red-50' 
            : 'bg-gray-50 hover:bg-gray-100'
        }`}
      >
        <h3 className={`text-lg font-bold ${expandedSection === name ? 'text-red-600' : 'text-gray-800'}`}>
          {title}
        </h3>
        {expandedSection === name ? (
          <ChevronUp className="w-5 h-5 text-red-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {expandedSection === name && (
        <div className="p-6 bg-white">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {mode === 'add' ? 'Create New FO Segment' : 'Edit FO Segment'}
              </h1>
              <p className="text-gray-600 mt-1">Fill in all required information</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleReset}
            className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="space-y-4">
              <Section title="Basic Information" name="basic">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Span ID <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="spanId"
                      value={formData.spanId}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.spanId ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., S000051315"
                    />
                    {errors.spanId && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠</span> {errors.spanId}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Segment Type <span className="text-red-600">*</span>
                    </label>
                    <select
                      name="segmentType"
                      value={formData.segmentType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    >
                      <option value="Insert">Insert</option>
                      <option value="Ring">Ring</option>
                      <option value="Star">Star</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Project Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="projectName"
                      value={formData.projectName}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.projectName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., INOF_Insert Site Fiberization 2025"
                    />
                    {errors.projectName && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠</span> {errors.projectName}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                      placeholder="Span description..."
                    />
                  </div>
                </div>
              </Section>

              <Section title="Location Information" name="location">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Kota/Kabupaten</label>
                    <input
                      type="text"
                      name="kota"
                      value={formData.kota}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., Kota Semarang"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Province</label>
                    <input
                      type="text"
                      name="province"
                      value={formData.province}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., Jawa Tengah"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Region</label>
                    <select
                      name="region"
                      value={formData.region}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    >
                      <option value="CENTRAL">CENTRAL</option>
                      <option value="EAST">EAST</option>
                      <option value="WEST">WEST</option>
                    </select>
                  </div>
                </div>
              </Section>

              <Section title="Site A Information" name="siteA">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Site ID A <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="siteIdA"
                      value={formData.siteIdA}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.siteIdA ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., JAW-JT-SMG-8686"
                    />
                    {errors.siteIdA && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠</span> {errors.siteIdA}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Site Name A</label>
                    <input
                      type="text"
                      name="siteNameA"
                      value={formData.siteNameA}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., SF-ZSMG_1018-BLANCIR"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Longitude A</label>
                    <input
                      type="text"
                      name="longA"
                      value={formData.longA}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., 110.4826"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Latitude A</label>
                    <input
                      type="text"
                      name="latA"
                      value={formData.latA}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="Optional"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Tower Owner A</label>
                    <input
                      type="text"
                      name="towerOwnerA"
                      value={formData.towerOwnerA}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., PT INTI BANGUN SEJAHTERA"
                    />
                  </div>
                </div>
              </Section>

              <Section title="Site B Information" name="siteB">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Site ID B <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="siteIdB"
                      value={formData.siteIdB}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.siteIdB ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., JAW-JT-DMK-0539"
                    />
                    {errors.siteIdB && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠</span> {errors.siteIdB}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Site Name B</label>
                    <input
                      type="text"
                      name="siteNameB"
                      value={formData.siteNameB}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., MRANGGEN_BATURSARI"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Longitude B</label>
                    <input
                      type="text"
                      name="longB"
                      value={formData.longB}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., 110.4883"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Latitude B</label>
                    <input
                      type="text"
                      name="latB"
                      value={formData.latB}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="Optional"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Tower Owner B</label>
                    <input
                      type="text"
                      name="towerOwnerB"
                      value={formData.towerOwnerB}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., PT CENTRATAMA MENARA INDONESIA"
                    />
                  </div>
                </div>
              </Section>

              <Section title="Technical & Project Information" name="technical">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Length Plan (meters) <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      name="lengthPlan"
                      value={formData.lengthPlan}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        errors.lengthPlan ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., 3080"
                      min="1"
                    />
                    {errors.lengthPlan && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠</span> {errors.lengthPlan}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Plan Distance to Closure (m)</label>
                    <input
                      type="number"
                      name="planDistance"
                      value={formData.planDistance}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., 1100"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Vendor</label>
                    <input
                      type="text"
                      name="vendor"
                      value={formData.vendor}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., EBTEL"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Project Year</label>
                    <input
                      type="number"
                      name="projectYear"
                      value={formData.projectYear}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., 2025"
                      min="2020"
                      max="2100"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">SPK Date</label>
                    <input
                      type="date"
                      name="spkDate"
                      value={formData.spkDate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </Section>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-blue-600 via-red-600 to-blue-600"></div>

          {/* Form Actions */}
          <div className="p-6 bg-gray-50">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onBack}
                className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <Save className="w-5 h-5" />
                {isSubmitting ? 'Saving...' : (mode === 'add' ? 'Create Segment' : 'Update Segment')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FOSegmentForm;